import React, { useEffect, useRef, useState } from 'react';
import Datepicker from 'react-tailwindcss-datepicker';
import './CalendarDatepicker.scss';

interface CalendarDatepickerProps {
  selectedDate: Date | null;
  setSelectedDate: (date: Date) => void;
  datepickerOnChange: (
    value: { startDate: Date | string | null } | null,
    setSelectedDate: (date: Date) => void,
  ) => void;
  error?: boolean;
  disabledDates?: string[];
  disabled?: boolean;
  minDate?: Date;
  triggerRef?: React.RefObject<HTMLDivElement>; // Add this line
}

const CalendarDatepicker = ({
  selectedDate,
  setSelectedDate,
  datepickerOnChange,
  error,
  disabledDates = [],
  disabled = false,
  minDate,
  triggerRef, // Add this parameter
}: CalendarDatepickerProps) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [popoverDirection, setPopoverDirection] = useState<'up' | 'down'>('down');

  const calculatePopoverDirection = () => {
    // Use triggerRef if provided, otherwise fall back to wrapperRef
    const targetRef = triggerRef || wrapperRef;
    if (!targetRef.current) return;

    const rect = targetRef.current.getBoundingClientRect();
    const windowHeight = window.innerHeight;
    const spaceBelow = windowHeight - rect.bottom;
    const spaceAbove = rect.top;
    const estimatedPopoverHeight = 300; // Approximate height of the datepicker popover

    // If there's not enough space below, and there's more space above, show it upward
    if (spaceBelow < estimatedPopoverHeight && spaceAbove > spaceBelow) {
      setPopoverDirection('up');
    } else {
      setPopoverDirection('down');
    }
  };

  useEffect(() => {
    const inputEl = wrapperRef.current?.querySelector('input');
    if (inputEl) {
      // Add click event to calculate direction when datepicker is opened
      inputEl.onclick = calculatePopoverDirection;
    }

    // Recalculate on window resize only (remove scroll listener for fixed positioning)
    window.addEventListener('resize', calculatePopoverDirection);

    return () => {
      window.removeEventListener('resize', calculatePopoverDirection);
    };
  }, [triggerRef]); // Add triggerRef to dependencies

  return (
    <div ref={wrapperRef} className={`datepicker-container ${error ? 'error' : ''}`}>
      <Datepicker
        asSingle={true}
        useRange={false}
        value={{
          startDate: selectedDate,
          endDate: selectedDate,
        }}
        onChange={(value) => {
          const selectedStartDate = value?.startDate ? new Date(value.startDate) : null;

          const isDisabled =
            selectedStartDate &&
            disabledDates.some(
              (disabledDate) => new Date(disabledDate).toDateString() === selectedStartDate.toDateString(),
            );

          if (!isDisabled && selectedStartDate) {
            datepickerOnChange({ startDate: selectedStartDate }, setSelectedDate);
          }
        }}
        startFrom={selectedDate ?? new Date()}
        minDate={minDate}
        displayFormat={'MMM D, YYYY'}
        showFooter={true}
        configs={{
          footer: {
            cancel: 'Cancel',
            apply: 'Apply',
          },
        }}
        dateLooking='backward'
        disabledDates={disabledDates.map((date) => ({ startDate: new Date(date), endDate: new Date(date) }))}
        disabled={disabled}
        popoverDirection={popoverDirection}
      />
    </div>
  );
};

export default CalendarDatepicker;
